/**
 * Video Call Controls Component
 * Responsive call control buttons: mute, camera, speaker, end call
 * Supports compact mode for mini view
 */

import React from "react";
import { Button } from "@/components/ui/button";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  PhoneOff,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  videoCallControlClassName,
  videoCallDestructiveControlClassName,
  videoCallOverlayClassName,
} from "./video-call-theme";

interface VideoCallControlsProps {
  isMuted: boolean;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onEndCall: () => void;
  className?: string;
  compact?: boolean;
}

export const VideoCallControls: React.FC<VideoCallControlsProps> = ({
  isMuted,
  isVideoEnabled,
  isAudioEnabled,
  onToggleMute,
  onToggleVideo,
  onToggleAudio,
  onEndCall,
  className,
  compact = false,
}) => {
  const buttonSize = compact ? "h-8 w-8" : "h-10 w-10 md:h-11 md:w-11";
  const iconSize = compact ? "h-3.5 w-3.5" : "h-4 w-4 md:h-4 md:w-4";
  const gapSize = compact ? "gap-2" : "gap-2 md:gap-4";
  const paddingSize = compact ? "px-2 py-2" : "px-4 py-3 md:px-6";

  return (
    <div
      className={cn(
        `flex items-center justify-center ${gapSize} ${paddingSize} rounded-full`,
        videoCallOverlayClassName,
        className
      )}
    >
      {/* Mute Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleMute}
        aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
        className={cn(
          `${buttonSize} rounded-full transition-colors`,
          isMuted
            ? videoCallDestructiveControlClassName
            : videoCallControlClassName
        )}
        title={isMuted ? "Unmute" : "Mute"}
      >
        {isMuted ? (
          <MicOff className={iconSize} />
        ) : (
          <Mic className={iconSize} />
        )}
      </Button>

      {/* Video Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleVideo}
        aria-label={isVideoEnabled ? "Turn off camera" : "Turn on camera"}
        className={cn(
          `${buttonSize} rounded-full transition-colors`,
          !isVideoEnabled
            ? videoCallDestructiveControlClassName
            : videoCallControlClassName
        )}
        title={isVideoEnabled ? "Turn off camera" : "Turn on camera"}
      >
        {isVideoEnabled ? (
          <Video className={iconSize} />
        ) : (
          <VideoOff className={iconSize} />
        )}
      </Button>

      {/* Audio/Speaker Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleAudio}
        aria-label={isAudioEnabled ? "Mute speaker" : "Unmute speaker"}
        className={cn(
          `${buttonSize} rounded-full transition-colors`,
          !isAudioEnabled
            ? videoCallDestructiveControlClassName
            : videoCallControlClassName
        )}
        title={isAudioEnabled ? "Mute speaker" : "Unmute speaker"}
      >
        {isAudioEnabled ? (
          <Volume2 className={iconSize} />
        ) : (
          <VolumeX className={iconSize} />
        )}
      </Button>

      {/* End Call Button - Always enabled so user can end call anytime */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onEndCall}
        aria-label="End call"
        className={cn(
          `${buttonSize} rounded-full transition-colors`,
          videoCallDestructiveControlClassName
        )}
        title="End call"
      >
        <PhoneOff className={iconSize} />
      </Button>
    </div>
  );
};

export default VideoCallControls;
